import { createObject } from 'src/utils/objects'

import endpoints from './endpoints'

const SEARCH_STORAGE_KEY = 'IADOPS_SEARCH'
const MAX_RECENT_SEARCHES = 4

const SEARCH_CODE_KEY = 'code'
const SEARCH_ID_KEY = 'associationId'
const SEARCH_TEXT_KEY = 'text'
const SEARCH_TEXT_ALT = 'textAlt'
const SEARCH_NETWORK_KEY = 'networkCode'

const search = {
  endpoints,
  config: {
    MAX_RECENT_SEARCHES,
    SEARCH_STORAGE_KEY,
    SEARCH_ID_KEY,
    SEARCH_TEXT_ALT,
    SEARCH_TEXT_KEY,
    SEARCH_OPTIONS: {
      // distance * threshold = character index
      distance: 300,
      includeMatches: true,
      isCaseSensitive: false,
      location: 0,
      maxPatternLength: 64,
      minMatchCharLength: 1,
      shouldSort: true,
      threshold: 0.2,
      useExtendedSearch: true,
      keys: [
        {
          name: SEARCH_TEXT_ALT,
          weight: 0.3,
        },
        {
          name: SEARCH_TEXT_KEY,
          weight: 0.2,
        },
        {
          name: SEARCH_NETWORK_KEY,
          weight: 0.2,
        },
        {
          name: SEARCH_CODE_KEY,
          weight: 0.2,
        },
        {
          name: SEARCH_ID_KEY,
          weight: 0.1,
        },
      ],
    },
  },

  alreadyChunked(indexStart, indexEnd, previousIndices) {
    return previousIndices.reduce((foundInstance, previousIndex) => {
      const [previousIndexStart, previousIndexEnd] = previousIndex
      return foundInstance || (indexStart >= previousIndexStart && indexEnd <= previousIndexEnd)
    }, false)
  },

  handleStartToFirstMatch(chunks, itemName, nameMatch) {
    const firstIndex = nameMatch.indices[0]
    const [start] = firstIndex
    const firstMatchIsAtBeginning = start === 0

    return (firstMatchIsAtBeginning === false && [0, start]) || [null, null]
  },

  handleLastMatchToEnd(chunks, itemName, nameMatch) {
    const lastIndex = nameMatch.indices[nameMatch.indices.length - 1]
    const [, end] = lastIndex
    const lastMatchIsAtEnd = end === itemName.length - 1

    return (lastMatchIsAtEnd === false && [end + 1, itemName.length - 1]) || [null, null]
  },

  handleInBetweenPiece(chunks, itemName, currentIndex, previousIndices) {
    if (previousIndices && previousIndices.length) {
      const [currentIndexStart] = currentIndex
      const lastIndex = previousIndices[previousIndices.length - 1]
      const [, lastIndexEnd] = lastIndex
      const proposedIndex = [lastIndexEnd + 1, currentIndexStart]
      const indexStart = Math.min(proposedIndex[0], proposedIndex[1])
      const indexEnd = Math.max(proposedIndex[0], proposedIndex[1])

      return [indexStart, indexEnd]
    }

    return [null, null]
  },

  handleMatch(chunks, itemName, currentIndex) {
    const [currentIndexStart, currentIndexEnd] = currentIndex
    const proposedIndex = [currentIndexStart, currentIndexEnd + 1]

    return proposedIndex
  },

  parseMatchesForResult(searchTextKey, result) {
    const itemText = result.item[searchTextKey]
    const chunks = []

    const nameMatch = result.matches
      ? result.matches.find((x) => x.key === searchTextKey)
      : null
    const hasNameMatch = !!nameMatch

    if (hasNameMatch) {
      const proposedIndices = {
        matches: [],
        nonMatches: [
          this.handleStartToFirstMatch(chunks, itemText, nameMatch),
        ],
      }

      nameMatch.indices.forEach((currentIndex) => {
        const matchIndex = this.handleMatch(chunks, itemText, currentIndex, proposedIndices)
        const nonMatchIndex = this.handleInBetweenPiece(chunks, itemText, currentIndex, proposedIndices)

        proposedIndices.matches.push(matchIndex)
        proposedIndices.nonMatches.push(currentIndex, nonMatchIndex)
      })

      proposedIndices.matches.push(this.handleLastMatchToEnd(chunks, itemText, nameMatch))

      const characters = itemText.split('')

      characters.forEach((letter, characterIndex) => {
        proposedIndices.matches.forEach((proposedIndex) => {
          const [proposedIndexStart, proposedIndexEnd] = proposedIndex
          const haveProposedIndex = proposedIndexStart !== null && proposedIndexEnd !== null
          const insertLetter = haveProposedIndex && (characterIndex >= proposedIndexStart && characterIndex <= proposedIndexEnd)

          if (insertLetter) {
            chunks.splice(characterIndex, 1, {
              value: letter,
              isMatched: true,
            })
          } else if (chunks[characterIndex] === undefined) {
            chunks.splice(characterIndex, 1, {
              value: letter,
              isMatched: false,
            })
          }
        })
      })
    } else {
      chunks.push({
        value: itemText,
        isMatched: false,
      })
    }

    return chunks
  },

  get(searchTextKey, searchTerm, items) {
    const matches = items.search ? items.search(searchTerm) : []
    return matches.map((result) => ({
      ...result.item,
      chunks: this.parseMatchesForResult(searchTextKey, result),
    }))
  },

  /**
   * Configures a function that builds a search option object from an item.
   * @param {Object} endpoint
   * @param {string} endpoint.baseReportName
   * @param {string} endpoint.name
   * @param {string} endpoint.routeName
   * @param {function} endpoint.text
   * @param {Object} endpoint.sub
   * @param {string} endpoint.sub.id
   * @param {string} endpoint.sub.code
   * @param {function} endpoint.sub.text
   * @returns {function(Object, Object): Object} A function that shapes a SearchOption from a team, a report, and a search endpoint.
   */
  mapOption(endpoint) {
    const { altCode, associationId, code, sub, subId, text } = endpoint
    return (teamOption, reportOption) => {
      const { reportLabel } = reportOption
      const option = createObject(teamOption, reportOption)

      option.associationId = option[associationId]
      option.baseReportName = endpoint.baseReportName || null
      option.routeName = endpoint.routeName || null

      const isSubOption = sub && option[sub.id]

      if (isSubOption) {
        const subtext = sub.text(option)
        const optionSubId = option[sub.id]

        option.code = option[sub.code]
        option.subId = typeof optionSubId === 'string' ? optionSubId : String(optionSubId)
        option.text = reportLabel ? `${subtext}: ${reportLabel}` : subtext
        option.textAlt = `${endpoint.name}: ${option.text}`
      } else {
        const optionLabel = text(option)
        const getDefaultSubId = () => ((associationId === 'all-associations' && 'all-secondary') || '-1')
        const optionSubId = (subId && (option[subId] || getDefaultSubId())) || null

        option.code = option[code]
        option.subId = optionSubId
        option.text = reportLabel ? `${optionLabel}: ${reportLabel}` : optionLabel
        option.textAlt = `${endpoint.name}: ${option.text}`
      }

      if (altCode) { option.altCode = option[altCode] }

      return option
    }
  },

  getEndpoints(searchTextKey, searchTerm, total) {
    return (searchOptions, endpointName) => {
      const endpoint = searchOptions[endpointName]
      const { items, recent } = endpoint

      const matches = search.get(searchTextKey, searchTerm, items)
      const recentMatches = search.get(searchTextKey, searchTerm, recent)

      const lengthItems = items.length
      const lengthRecent = recentMatches.length
      const lengthResults = matches.length

      searchOptions[endpointName].results = matches
      searchOptions[endpointName].recent = recentMatches
      searchOptions[endpointName].total = {
        items: lengthItems,
        recent: lengthRecent,
        results: lengthResults,
      }

      total.items += lengthItems
      total.recent += lengthRecent
      total.results += lengthResults

      return searchOptions
    }
  },
  hasEndpointAccess(rootGetters, navigationEndpoint) {
    const { access } = navigationEndpoint

    if (typeof access !== 'function') return false

    return access(rootGetters)
  },
}

export default search
